import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Select } from 'antd';

import { searchTemas, type Tema } from '../../../services/admin';

const DEBOUNCE_MS = 300;

export interface TemaSelectProps {
  /** Com edição: lista os temas dela. Sem: busca por texto em todos. */
  edicoesId?: number;
  value?: number;
  valueLabel?: string | null;
  onChange: (id?: number, tema?: Tema) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  allowClear?: boolean;
}

export const TemaSelect: React.FC<TemaSelectProps> = ({
  edicoesId,
  value,
  valueLabel,
  onChange,
  placeholder = 'Tema',
  style,
  allowClear = true,
}) => {
  const [temas, setTemas] = useState<Tema[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const requestId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // Trocar a edição invalida as opções (e qualquer busca em voo).
  useEffect(() => {
    requestId.current += 1;
    if (timer.current) clearTimeout(timer.current);
    setTemas([]);
    setLoading(false);
  }, [edicoesId]);

  const load = useCallback(
    (text: string) => {
      if (edicoesId === undefined && !text) {
        setTemas([]);
        return;
      }
      const id = ++requestId.current;
      const params =
        edicoesId === undefined
          ? { q: text, limit: 20 }
          : { edicoesId, ...(text ? { q: text } : {}), limit: 50 };
      setLoading(true);
      searchTemas(params)
        .then((result) => {
          if (mounted.current && id === requestId.current) setTemas(result);
        })
        .catch(() => {
          if (mounted.current && id === requestId.current) setTemas([]);
        })
        .finally(() => {
          if (mounted.current && id === requestId.current) setLoading(false);
        });
    },
    [edicoesId],
  );

  const handleSearch = (text: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(text.trim()), DEBOUNCE_MS);
  };

  const options = temas.map((t) => ({ value: t.id, label: t.nome ?? `Tema ${t.id}` }));
  if (value !== undefined && !temas.some((t) => t.id === value)) {
    options.unshift({ value, label: valueLabel ?? `Tema ${value}` });
  }

  return (
    <Select
      showSearch
      filterOption={false}
      allowClear={allowClear}
      placeholder={placeholder}
      style={style}
      loading={loading}
      value={value}
      options={options}
      notFoundContent={loading ? 'Buscando…' : edicoesId === undefined ? 'Digite para buscar' : 'Nenhum tema'}
      onDropdownVisibleChange={(open) => {
        if (open) load('');
      }}
      onSearch={handleSearch}
      onChange={(id?: number) => onChange(id, temas.find((t) => t.id === id))}
    />
  );
};
